'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSlide {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  alt: string;
  cta: string;
  ctaLink: string;
}

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const heroSlides: HeroSlide[] = [
  {
    id: 1,
    title: "Infrastructure You Can Depend On",
    subtitle: "Telecom Tower Installation & Maintenance",
    description: "Delivering mission-critical infrastructure solutions with 99.9% uptime guarantee across 500+ installations nationwide",
    image: "https://images.unsplash.com/photo-1538962526599-a04a470e1936",
    alt: "Tall telecommunications tower against blue sky with multiple antenna arrays and microwave dishes",
    cta: "Explore Our Projects",
    ctaLink: "/services-overview"
  },
  {
    id: 2,
    title: "Technical Excellence, Delivered Reliably",
    subtitle: "Electrical Systems & Power Solutions",
    description: "Certified electrical contractors specializing in HT/LT installations, DG sets, and critical power infrastructure",
    image: "https://images.unsplash.com/photo-1615774925655-a0e97fc85c14",
    alt: "Professional electrician in safety gear working on industrial electrical control panel with circuit breakers",
    cta: "View Our Capabilities",
    ctaLink: "/services-overview"
  },
  {
    id: 3,
    title: "Your Trusted Infrastructure Partner",
    subtitle: "Comprehensive AMC & Maintenance Services",
    description: "24/7 proactive support with 2-hour emergency response time for telecom and electrical infrastructure",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f7d65b98-1767604302628.png",
    alt: "Field engineer in hard hat inspecting telecommunications equipment with digital tablet in outdoor facility",
    cta: "Request Service",
    ctaLink: "/contact-and-support"
  }];


  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isHydrated, heroSlides.length]);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const handleDotClick = (index: number) => {
    setCurrentSlide(index);
  };

  if (!isHydrated) {
    return (
      <section className="relative h-[600px] lg:h-[700px] bg-muted">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/70" />
      </section>);

  }

  return (
    <section className="relative h-[600px] lg:h-[700px] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <AppImage
          src={heroSlides[currentSlide].image}
          alt={heroSlides[currentSlide].alt}
          className="w-full h-full object-cover"
          priority />

        <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/70" />
      </div>

      {/* Content */}
      <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col justify-center h-full max-w-3xl">
          <div className="space-y-6">
            <div className="inline-flex items-center px-4 py-2 bg-accent/20 backdrop-blur-sm rounded-full border border-accent/30">
              <Icon name="CheckBadgeIcon" size={20} className="text-accent mr-2" />
              <span className="text-sm font-medium text-white">ISO 9001:2015 Certified</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-headline font-bold text-white leading-tight">
              {heroSlides[currentSlide].title}
            </h1>

            <h2 className="text-xl sm:text-2xl lg:text-3xl font-headline font-semibold text-accent">
              {heroSlides[currentSlide].subtitle}
            </h2>

            <p className="text-lg sm:text-xl text-white/90 font-body leading-relaxed">
              {heroSlides[currentSlide].description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <a
                href={heroSlides[currentSlide].ctaLink}
                className="inline-flex items-center justify-center px-8 py-4 bg-cta text-cta-foreground rounded-md font-cta font-semibold text-lg shadow-cta transition-all duration-300 hover:-translate-y-1 hover:shadow-modal">

                {heroSlides[currentSlide].cta}
                <Icon name="ArrowRightIcon" size={20} className="ml-2" />
              </a>
              <a
                href="/contact-and-support"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-md font-body font-semibold text-lg border-2 border-white/30 transition-all duration-300 hover:bg-white/20">

                <Icon name="PhoneIcon" size={20} className="mr-2" />
                24/7 Emergency Support
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      <button
        onClick={handlePrevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full border border-white/30 text-white transition-all duration-300 hover:bg-white/20 hover:scale-110"
        aria-label="Previous slide">

        <Icon name="ChevronLeftIcon" size={24} />
      </button>

      <button
        onClick={handleNextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full border border-white/30 text-white transition-all duration-300 hover:bg-white/20 hover:scale-110"
        aria-label="Next slide">

        <Icon name="ChevronRightIcon" size={24} />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3">
        {heroSlides.map((_, index) =>
        <button
          key={index}
          onClick={() => handleDotClick(index)}
          className={`transition-all duration-300 rounded-full ${
          index === currentSlide ?
          'w-12 h-3 bg-accent' : 'w-3 h-3 bg-white/50 hover:bg-white/70'}`
          }
          aria-label={`Go to slide ${index + 1}`} />

        )}
      </div>
    </section>);

};

export default HeroSection;